import type { CSSProperties } from "react"

interface StyleProps {
  backgroundColor?: string
  overlayColor?: string
  textColor?: string
  buttonColor?: string
  buttonTextColor?: string
  fontFamily?: string
  headingFontFamily?: string
  /** Keep the overlay under Google’s challenge iframe (z-index 2000000000). */
  recaptchaEnabled?: boolean
}

export const resetStyles: CSSProperties = {
  boxSizing: "border-box",
  margin: "0",
  padding: "0",
  letterSpacing: "normal",
}

const DEFAULT_CARD = "#d4ecec"
const DEFAULT_TEXT = "#111111"
const DEFAULT_BUTTON = "#000000"
const DEFAULT_BUTTON_TEXT = "#ffffff"
const DEFAULT_SANS =
  'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'
const DEFAULT_SERIF = 'Georgia, "Times New Roman", Times, serif'

export const createStyles = (props: StyleProps): Record<string, CSSProperties> => {
  const sans = props.fontFamily || DEFAULT_SANS
  const serif = props.headingFontFamily || DEFAULT_SERIF
  const textColor = props.textColor || DEFAULT_TEXT

  return {
    overlay: {
      ...resetStyles,
      fontFamily: sans,
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      minHeight: "100vh",
      maxWidth: "100vw",
      zIndex: props.recaptchaEnabled ? 1999999990 : 2147483647,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: props.overlayColor || "rgba(0, 0, 0, 0.55)",
      overflow: "auto",
      WebkitTextSizeAdjust: "100%",
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
      padding: "1.5rem",
    },

    card: {
      ...resetStyles,
      fontFamily: sans,
      position: "relative",
      width: "100%",
      maxWidth: "440px",
      minHeight: "480px",
      justifyContent: "center",
      backgroundColor: props.backgroundColor || DEFAULT_CARD,
      color: textColor,
      padding: "3rem 2.25rem 1.5rem",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
    },

    closeButton: {
      ...resetStyles,
      position: "absolute",
      top: "14px",
      right: "14px",
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      border: "none",
      backgroundColor: "#cfcfcf",
      color: "#4a4a4a",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "0",
      lineHeight: 1,
    },

    formTitle: {
      ...resetStyles,
      fontFamily: serif,
      fontSize: "30px",
      fontWeight: 700,
      lineHeight: 1.25,
      color: textColor,
      maxWidth: "22ch",
    },

    formSubtitle: {
      ...resetStyles,
      fontFamily: sans,
      fontSize: "15px",
      fontWeight: 400,
      lineHeight: 1.4,
      color: textColor,
      marginTop: "0.85rem",
      marginBottom: "1.35rem",
    },

    form: {
      ...resetStyles,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      textAlign: "left",
    },

    inputGroup: {
      ...resetStyles,
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },

    textInput: {
      ...resetStyles,
      fontFamily: serif,
      width: "100%",
      height: "48px",
      padding: "0 14px",
      fontSize: "16px",
      backgroundColor: "#ffffff",
      color: "#111111",
      border: "1px solid #e6e6e6",
      borderRadius: "0",
      outline: "none",
      appearance: "none",
      WebkitAppearance: "none",
    },

    stateLabel: {
      ...resetStyles,
      fontFamily: sans,
      fontSize: "15px",
      fontWeight: 700,
      color: textColor,
      marginTop: "1.15rem",
      marginBottom: "0.75rem",
    },

    stateGrid: {
      ...resetStyles,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      columnGap: "1.25rem",
      rowGap: "0.7rem",
      width: "100%",
    },

    radioRow: {
      ...resetStyles,
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
      cursor: "pointer",
      fontFamily: serif,
      fontSize: "16px",
      color: textColor,
      userSelect: "none",
    },

    radio: {
      ...resetStyles,
      width: "16px",
      height: "16px",
      flexShrink: 0,
      accentColor: textColor,
      cursor: "pointer",
    },

    consentList: {
      ...resetStyles,
      display: "flex",
      flexDirection: "column",
      gap: "0.65rem",
      width: "100%",
      marginTop: "1.15rem",
    },

    consentRow: {
      ...resetStyles,
      display: "flex",
      alignItems: "flex-start",
      gap: "0.5rem",
      cursor: "pointer",
      fontFamily: sans,
      fontSize: "12px",
      lineHeight: 1.4,
      color: textColor,
      userSelect: "none",
    },

    consentCheckbox: {
      ...resetStyles,
      width: "16px",
      height: "16px",
      marginTop: "1px",
      flexShrink: 0,
      accentColor: textColor,
      cursor: "pointer",
    },

    consentRequired: {
      ...resetStyles,
      color: "#9b1c1c",
      fontSize: "16px",
      fontWeight: 700,
      lineHeight: 1,
      marginLeft: "0.15em",
    },

    submitButton: {
      ...resetStyles,
      fontFamily: serif,
      alignSelf: "center",
      marginTop: "1.75rem",
      minWidth: "168px",
      height: "46px",
      padding: "0 2rem",
      border: "none",
      borderRadius: "0",
      backgroundColor: props.buttonColor || DEFAULT_BUTTON,
      color: props.buttonTextColor || DEFAULT_BUTTON_TEXT,
      fontSize: "20px",
      fontWeight: 700,
      cursor: "pointer",
      lineHeight: 1,
    },

    error: {
      ...resetStyles,
      fontFamily: sans,
      fontSize: "13px",
      color: "#9b1c1c",
      textAlign: "center",
      marginTop: "0.75rem",
    },

    recaptcha: {
      ...resetStyles,
      fontFamily: sans,
      fontSize: "11px",
      color: textColor,
      textAlign: "center",
      marginTop: "1.75rem",
    },

    recaptchaLink: {
      color: "inherit",
      textDecoration: "underline",
    },

    confirmationTitle: {
      ...resetStyles,
      fontFamily: serif,
      fontSize: "26px",
      fontWeight: 700,
      lineHeight: 1.3,
      color: textColor,
      maxWidth: "20ch",
    },

    confirmationMessage: {
      ...resetStyles,
      fontFamily: sans,
      fontSize: "15px",
      fontWeight: 400,
      lineHeight: 1.45,
      color: textColor,
      marginTop: "1rem",
      maxWidth: "34ch",
    },

    confirmationButton: {
      ...resetStyles,
      fontFamily: serif,
      marginTop: "1.75rem",
      minWidth: "168px",
      height: "46px",
      padding: "0 1.5rem",
      border: "none",
      borderRadius: "0",
      backgroundColor: props.buttonColor || DEFAULT_BUTTON,
      color: props.buttonTextColor || DEFAULT_BUTTON_TEXT,
      fontSize: "18px",
      fontWeight: 700,
      cursor: "pointer",
      lineHeight: 1,
    },
  }
}
